import { useAppSelector } from "hooks"
import { useCallback, useEffect, useState } from "react"
import { Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { hiabColors } from "theme"
import type { ILicensePool } from "types/licensePoolTypes"

type Props = {
  licensePool: ILicensePool
  forecastMonths: number
}

interface IAvailableLicenses {
  date: string
  count: number
  max: number
}

export function LicensePoolAvailabilityChart({ licensePool, forecastMonths }: Props) {
  const [chartData, setChartData] = useState<Array<IAvailableLicenses>>([])
  const licenses = useAppSelector((state) => state.license.licenses)
  const licensesInPool = licenses.filter((x) => x.licensePoolId === licensePool.id)

  /**
   * Returns all licenses within the pool where cancelationDate is null (not assigned),
   * and where licenseCanceled is true and cancelationDate < date
   */
  const getAvailableLicensesAtTime = useCallback(
    (date: Date): number => {
      return licensesInPool.filter(
        (x) =>
          x.cancelationDate === null || (x.licenseCanceled && new Date(x.cancelationDate) < date),
      ).length
    },
    [licensesInPool],
  )

  useEffect(() => {
    const data = Array.from({ length: forecastMonths }, (_, i) => {
      const now = new Date()
      const d = new Date(now.getFullYear(), now.getMonth() + i)

      return {
        date: d.toLocaleDateString(navigator.language, { year: "numeric", month: "numeric" }),
        count: getAvailableLicensesAtTime(d),
        max: licensesInPool.length,
      }
    })

    setChartData(data)
  }, [forecastMonths, getAvailableLicensesAtTime, licensesInPool.length])

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chartData}>
        {/* Using a line for the reference here instead of ReferenceLine, because ReferenceLine does not allow legends */}
        <Line
          dataKey="max"
          stroke={hiabColors.greyPrimary}
          name="Total licenses"
          activeDot={false}
          dot={false}
          legendType="plainline"
          tooltipType="none"
          strokeDasharray="5 3"
        />
        <Line dataKey="count" stroke={hiabColors.bluePrimary} name="Available licenses" />
        <Tooltip />
        <XAxis dataKey="date" />
        <YAxis allowDecimals={false} />
        <Legend />
      </LineChart>
    </ResponsiveContainer>
  )
}
